import { ifProp, switchProp, theme } from 'styled-tools';
import styled, { css } from 'styled-components';

import breakpoint from 'styled-components-breakpoint';
import { rem } from 'polished';

const CaseSection = styled.section`
  width: 100%;
  height: 100vh;
  max-height: 630px;
  overflow: hidden;
  color: ${theme('colors.white')};
  background-color: ${theme('colors.blue')};
  box-sizing: border-box;

  ${breakpoint('md')`
    max-height: 645px;
  `}
`;

type GradientDirection = 'left' | 'right' | 'bottom';

const desktopGradientPlacement: Record<GradientDirection, { angle: string; left: string; right: string }> = {
  left: { angle: '90deg', left: '0', right: '20%' },
  right: { angle: '-90deg', left: '20%', right: '0' },
  bottom: { angle: '0deg', left: '0', right: '0' },
};

const gradientOverlay = ({
  angle,
  left,
  right,
  color,
}: {
  angle: string;
  left: string;
  right: string;
  color?: string;
}) => css`
  &:after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: ${left};
    right: ${right};
    background: linear-gradient(${angle}, ${color} -10%, rgba(47, 63, 86, 0) 92.62%);
  }
`;

const CaseContainer = styled.div<{
  layout?: 'left' | 'right' | 'center';
  bgImage?: string;
  useGradient?: GradientDirection;
  gradientColor?: string;
  useMobileGradient?: boolean;
  bgPositionTwo?: boolean;
}>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  position: relative;

  ${({ bgPositionTwo }) =>
    bgPositionTwo &&
    css`
      background-position: right;
    `}

  ${({ bgImage }) =>
    bgImage &&
    css`
      background-image: url(${bgImage});
    `}

  ${({ useGradient, gradientColor }) =>
    useGradient && gradientOverlay({ ...desktopGradientPlacement[useGradient], color: gradientColor })}

  .content-wrapper {
    width: 100%;
    height: 100%;
    padding-top: 4rem;
    display: flex;
    position: relative;
    z-index: 1;
    flex-direction: column;
    justify-content: flex-start;

    ${({ layout }) => css`
      align-items: ${layout === 'left' ? 'flex-start' : layout === 'right' ? 'flex-end' : 'center'};
      text-align: ${layout === 'left' ? 'left' : layout === 'right' ? 'right' : 'center'};
    `}
  }

  .cta-wrapper {
    position: relative;
    margin-top: 1.875rem;
    margin-bottom: 0;
  }

  ${breakpoint('lg')`
    padding-right: 6rem;
    padding-left: 6rem;
  `}
  ${breakpoint('md', 'lg')`
    padding-right: 3.6rem;
    padding-left: 3.6rem;
  `}
  ${breakpoint('sm', 'md')`
    padding-right: 3.6rem;
    padding-left: 3.6rem;

    ${({ layout }) =>
      layout === 'left'
        ? css`
            background-position: left;
            background-size: auto;
          `
        : css`
            background-position: 90%;
            background-size: cover;
          `}
  `}
  ${breakpoint('xs', 'sm')`
    padding-right: 1.5rem;
    padding-left: 1.5rem;
    background-position: ${({ bgPositionTwo }) => (bgPositionTwo ? '30% 100%' : 'right')};

    .content-wrapper {
      align-items: flex-start;
      text-align: left;
    }

    .cta-wrapper {
      a {
        font-size: 17px;
      }
    }

    ${({ useMobileGradient, gradientColor }) =>
      useMobileGradient && gradientOverlay({ angle: '360deg', left: '0', right: '0', color: gradientColor })}
  `}
`;

const Blockquote = styled.blockquote<{ mode?: string; longContent?: boolean }>`
  position: relative;
  padding: 0;
  margin: 0;
  max-width: ${ifProp({ longContent: true }, '650px', '600px')};
  width: ${ifProp({ longContent: true }, '60%', '50%')};
  font-size: ${rem(48)};
  color: ${ifProp({ mode: 'light' }, theme('colors.white'), theme('colors.blue'))};

  svg {
    display: inline;
    fill: none;
  }

  .quote-wrapper {
    position: relative;
    margin-bottom: 1.3rem;
  }

  ${breakpoint('xs', 'sm')`
    width: 100%;
    color: #fff;

    .quote-wrapper {
      margin-bottom: 2.5rem;
    }
  `};
`;

const Heading = styled.p<{ mode?: string }>`
  display: inline-block;
  color: ${ifProp({ mode: 'light' }, theme('colors.white'), theme('colors.blue'))};
  font-weight: bold;
  line-height: 75px;
  box-decoration-break: clone;

  ${breakpoint('md')`
    font-size: 1em;
    line-height: 1.3em;
  `}
  ${breakpoint('sm', 'md')`
    font-size: 0.7em;
    line-height: 1.4em;
  `}
  ${breakpoint('xs', 'sm')`
    font-size: 0.6em;
    line-height: 1.4em;
    color: #fff;
    margin-bottom: 0;
  `}
`;

const Cite = styled.cite<{ mode?: string }>`
  display: block;
  font-style: normal;
  color: ${ifProp({ mode: 'light' }, theme('colors.white'), theme('colors.blue'))};
  font-size: ${rem(20)};
  margin-top: 0.5rem;

  ${breakpoint('xs', 'sm')`
    color: #fff;
    margin-top: 1.875rem;
  `};
`;

const Quoteline = styled.p<{ mode?: string }>`
  font-style: italic;
  font-size: 18px;
  color: ${ifProp({ mode: 'light' }, theme('colors.white'), theme('colors.blue'))};
  padding-top: 0.5em;
  margin-bottom: 0;

  ${breakpoint('xs', 'sm')`
    color: #fff;
    display: none;
  `};
`;

const QuoteMarkLine = styled.hr<{
  layoutLine?: string;
  layout?: 'left' | 'right' | 'center';
}>`
  background: ${theme('colors.orange')};
  position: absolute;
  padding: 0;
  margin: 0;
  border: 0;

  ${switchProp(
    'layout',
    {
      center: css`
        height: 64px;
        width: 3px;
        left: calc(50% - 3px);
        top: -64px;
      `,
    },
    css`
      height: 3px;
      width: 200px;
    `,
  )}

  ${switchProp('layoutLine', {
    left: css`
      top: calc(50% - 3px);
      left: -195px;
    `,
    right: css`
      top: calc(50% - 3px);
      right: -195px;
    `,
    // review top and bottom
    top: css`
      transform: rotate(90deg);
      top: -195px;
      left: calc(50% - 3px);
    `,
    bottom: css`
      bottom: -195px;
      left: calc(50% - 3px);
    `,
    none: css`
      display: none;
    `,
  })}

  ${breakpoint('xs', 'sm')`
    left: -195px;
    ${switchProp('layoutLine', {
      center: css`
        height: 3px;
        width: 200px;
        top: calc(50% - 3px);
      `,
    })}
  `};
`;

const Logo = styled.div<{
  layout?: 'left' | 'right' | 'center';
  logo?: string;
  widerLogo?: boolean;
}>`
  display: flex;
  margin-top: 1.875rem;
  width: 100%;

  ${switchProp(
    'layout',
    {
      left: css`
        justify-content: flex-start;
      `,
      right: css`
        justify-content: flex-end;
      `,
    },
    css`
      justify-content: center;
    `,
  )}

  img {
    margin-bottom: 0;
    ${switchProp(
      'widerLogo',
      {
        true: css`
          max-width: 150px;
        `,
      },
      css`
        max-width: 90px;
      `,
    )}
  }

  ${breakpoint('xs', 'sm')`
    justify-content: flex-start
  `};
`;

export { CaseSection, CaseContainer, Heading, Blockquote, Cite, Quoteline, QuoteMarkLine, Logo };
